import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'
import { useApiErrorText } from '@/lib/staffGate'

import { useAssignTable, useFreeTables } from './api'

/**
 * Donner un numéro de table, à l'arrivée du groupe.
 *
 * Pourquoi ici et pas à l'achat : on ne sait pas des semaines à l'avance qui
 * viendra ni comment la salle sera montée. On place les gens quand on les a
 * devant soi.
 *
 * L'écran est fait pour un contrôle d'entrée : de grosses cibles tactiles, le
 * bon type de table d'abord, et les autres accessibles sans chercher — une
 * salle réelle oblige parfois à déclasser un groupe, et l'interface ne doit
 * pas l'interdire au moment où ça se décide.
 */
export function TablePicker({
  reservationId,
  partySize,
  assignedNumber,
  assignedName = null,
  seatedAt = null,
  seatedBy = null,
  eventId,
  onAssigned,
}: {
  reservationId: string
  partySize: number | null
  assignedNumber: string | null
  /** Le nom de la table attribuée, s'il y en a un — on l'annonce au groupe. */
  assignedName?: string | null
  /** Le groupe est déjà installé : la table est bloquée, la porte l'annonce. */
  seatedAt?: string | null
  seatedBy?: string | null
  eventId: string | undefined
  onAssigned: (tableNumber: string | null, tableName: string | null) => void
}) {
  const { t, i18n } = useTranslation()
  const errorText = useApiErrorText()
  const [open, setOpen] = useState(assignedNumber === null)

  const free = useFreeTables(open ? reservationId : null, eventId)
  const assign = useAssignTable(eventId)

  const tables = free.data ?? []
  const matching = tables.filter((table) => table.matches_type)
  const others = tables.filter((table) => !table.matches_type)

  // Installé par l'hôtesse : la table est bloquée. La porte annonce le
  // numéro au groupe, mais ne le change plus — ce geste demande un motif,
  // et il se fait depuis l'écran Salle → Placement.
  if (assignedNumber && seatedAt) {
    return (
      <div className="mt-6 rounded-lg border border-state-success/40 bg-state-success/5 px-5 py-4">
        <p className="text-sm text-content-secondary">{t('checkin.table.assigned')}</p>
        <p className="mt-1 font-display text-display-md text-champagne-300">{assignedNumber}</p>
        {assignedName ? (
          <p className="text-heading-sm text-content-primary">{assignedName}</p>
        ) : null}
        <p className="mt-2 text-sm text-state-success">
          {t('checkin.table.seated', {
            time: new Date(seatedAt).toLocaleTimeString(i18n.language, {
              hour: '2-digit',
              minute: '2-digit',
            }),
            name: seatedBy ?? '—',
          })}
        </p>
      </div>
    )
  }

  if (assignedNumber && !open) {
    return (
      <div className="mt-6 rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4">
        <p className="text-sm text-content-secondary">{t('checkin.table.assigned')}</p>
        <p className="mt-1 font-display text-display-md text-champagne-300">{assignedNumber}</p>
        {assignedName ? (
          <p className="text-heading-sm text-content-primary">{assignedName}</p>
        ) : null}
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => setOpen(true)}>
          {t('checkin.table.change')}
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-6 rounded-lg border border-champagne-400/40 bg-champagne-400/5 px-5 py-4">
      <p className="text-sm text-champagne-300">
        {t('checkin.table.needed', { count: partySize ?? 0 })}
      </p>

      {assign.error ? (
        <p className="mt-3 text-sm text-state-danger">{errorText(assign.error)}</p>
      ) : null}

      {free.isPending ? (
        <p className="mt-3 text-sm text-content-muted">{t('state.loading')}</p>
      ) : tables.length === 0 ? (
        <p className="mt-3 text-sm text-state-warning">{t('checkin.table.none')}</p>
      ) : (
        <>
          <TableRow
            tables={matching}
            busy={assign.isPending}
            current={assignedNumber}
            onPick={(id, number, name) =>
              assign.mutate(
                { reservationId, physical_table_id: id },
                {
                  onSuccess: () => {
                    onAssigned(number, name)
                    setOpen(false)
                  },
                },
              )
            }
          />

          {others.length > 0 ? (
            <>
              <p className="mt-4 text-caption uppercase tracking-wide text-content-muted">
                {t('checkin.table.otherTypes')}
              </p>
              <TableRow
                tables={others}
                busy={assign.isPending}
                current={assignedNumber}
                onPick={(id, number, name) =>
                  assign.mutate(
                    { reservationId, physical_table_id: id },
                    {
                      onSuccess: () => {
                        onAssigned(number, name)
                        setOpen(false)
                      },
                    },
                  )
                }
              />
            </>
          ) : null}
        </>
      )}

      {assignedNumber ? (
        <Button
          variant="ghost"
          size="sm"
          className="mt-4"
          disabled={assign.isPending}
          onClick={() =>
            assign.mutate(
              { reservationId, physical_table_id: null },
              {
                onSuccess: () => {
                  onAssigned(null, null)
                  setOpen(true)
                },
              },
            )
          }
        >
          {t('checkin.table.release')}
        </Button>
      ) : null}
    </div>
  )
}

function TableRow({
  tables,
  busy,
  current,
  onPick,
}: {
  tables: {
    id: string
    table_number: string
    table_name: string | null
    zone: string | null
    capacity: number
  }[]
  busy: boolean
  current: string | null
  onPick: (id: string, tableNumber: string, tableName: string | null) => void
}) {
  return (
    <ul className="mt-3 flex flex-wrap gap-2">
      {tables.map((table) => (
        <li key={table.id}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(table.id, table.table_number, table.table_name)}
            aria-current={table.table_number === current}
            className={cn(
              // Cible tactile large : on appuie avec le pouce, dans le noir.
              'min-w-[4.5rem] rounded-lg border px-4 py-3 text-center transition',
              'disabled:opacity-50',
              table.table_number === current
                ? 'border-champagne-400 bg-bg-overlay text-content-primary'
                : 'border-line text-content-secondary hover:border-champagne-400/60 hover:text-content-primary',
            )}
          >
            <span className="block font-display text-heading-md">{table.table_number}</span>
            {table.table_name ? (
              <span className="block text-sm text-content-primary">{table.table_name}</span>
            ) : null}
            <span className="block text-caption text-content-muted">
              {table.capacity}
              {table.zone ? ` · ${table.zone}` : ''}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
